import React, {useEffect, useRef, useState} from 'react';

import * as Sentry from '@sentry/react';
import axios from 'axios';
import type {CancelTokenSource} from 'axios';
import {mapValues} from 'lodash';
import type {ReactCropperElement} from 'react-cropper';
import Cropper from 'react-cropper';
import {useIntl} from 'react-intl';

import * as mediaModule from '@edume/bento/media';
import {Button, HintBox, Link, LoadingIndicator} from '@edume/magnificent';

import {StyledModal} from '../../../containers/StyledModals';
import {useSelector} from '../../../state/hooks';
import {
  useAddImageToLibraryMutation,
  useDeleteFromImageLibraryMutation,
  useTrackImageUseMutation,
} from '../../../state/imageLibrary/imageLibraryApi';
import type {Image} from '../../../state/imageLibrary/imageLibraryApi';
import {createErrorNotification} from '../../../state/notification/notificationActions';
import {getSelectedGroupId} from '../../../state/team/teamSelectors';
import {contain, intersect2D} from '../../../utils/crop.js';
import {
  getImageDimensions,
  handleImageUpload,
  readURL,
} from '../../../utils/fileUpload.js';
import {ImageLibrary} from './ImageLibrary';
import ImagePreviewer from './ImagePreviewer';
import {NINE_BY_SIXTEEN_IMAGE_ONLY} from './mediaTypes';
import type {LastPickedFileState, StepName} from './types';
import getFileSizeErrorStep from './UploadMediaModalSteps/getFileSizeErrorStep';
import getFileSizeWarningStep from './UploadMediaModalSteps/getFileSizeWarningStep';
import {getLibraryStep} from './UploadMediaModalSteps/getLibraryStep';
import getUploadErrorStep from './UploadMediaModalSteps/getUploadErrorStep';
import getUploadPreviewStep from './UploadMediaModalSteps/getUploadPreviewStep';
import getUploadStep from './UploadMediaModalSteps/getUploadStep';
import {MEDIA_LIBRARY_TABS, useMediaLibraryTab} from './useMediaLibraryTab';
import type useUploadModal from './useUploadModal';
import type {MediaType} from './useUploadModal';

import styles from './styles.module.scss';
import 'cropperjs/dist/cropper.css';

type UploadImageModalProps = {
  cropAspectRatio: number;
  showOriginalImage: boolean;
  hintTextKey: string;
  isRemovingEnabled: boolean;
  mediaType: MediaType;
} & Pick<
  ReturnType<typeof useUploadModal>,
  | 'onSetImage'
  | 'onSetCroppedImage'
  | 'onSaveChanges'
  | 'onRemove'
  | 'onClose'
  | 'imageUrl'
  | 'croppedImageUrl'
>;

export const UploadImageModal = ({
  imageUrl,
  croppedImageUrl,
  cropAspectRatio,
  showOriginalImage,
  hintTextKey,
  onSetImage,
  onSetCroppedImage,
  onSaveChanges,
  onRemove,
  onClose,
  isRemovingEnabled,
  mediaType,
}: UploadImageModalProps) => {
  const initialStep = 'library';
  const intl = useIntl();
  const cropperRef = useRef<ReactCropperElement>(null);
  const axiosCancelSource = useRef<CancelTokenSource | null>(null);
  const [step, setStep] = useState<StepName>(() =>
    imageUrl || croppedImageUrl ? 'initial-seeded' : initialStep
  );
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isCropperLoading, setIsCropperLoading] = useState(true);
  const [libraryTab, setLibraryTab] = useMediaLibraryTab(
    MEDIA_LIBRARY_TABS.GROUP_IMAGES
  );
  const [lastPickedFile, setLastPickedFile] = useState<Extract<
    LastPickedFileState,
    {type: 'file' | 'image'}
  > | null>(null);

  const groupId = useSelector(getSelectedGroupId);

  const [addImageToLibrary] = useAddImageToLibraryMutation();
  const [deleteFromImageLibrary] = useDeleteFromImageLibraryMutation();
  const [trackImageUse] = useTrackImageUseMutation();

  useEffect(() => {
    axiosCancelSource.current = axios.CancelToken.source();
    return () => {
      axiosCancelSource.current?.cancel(
        mediaModule.CANCELLED_COMPONENT_UNMOUNTED
      );
    };
  }, []);

  const startUpload = async (files: File[], {ignoreWarning = false} = {}) => {
    if (files.length) {
      setStep('uploading');
      const file = files[0];
      const url = await readURL(file);
      const fileName = file.name;
      const {width, height} = await getImageDimensions(url);

      setLastPickedFile({
        type: 'file',
        content: file,
        url,
        width,
        height,
        groupId: groupId,
      });

      if (mediaModule.shouldErrorFileTooBig(file, 'image')) {
        setStep('file-size-error');
      } else if (
        mediaModule.shouldWarnFileTooBig(file, 'image') &&
        !ignoreWarning
      ) {
        setStep('file-size-warning');
      } else {
        handleImageUpload(file, axiosCancelSource.current)
          .then((uploadedImage) => {
            onSetImage(uploadedImage.url, {fileName, width, height});
            if (mediaType === NINE_BY_SIXTEEN_IMAGE_ONLY) {
              setStep('success');
            } else {
              setIsCropperLoading(true);
              setStep('crop');
            }
          })
          .catch((err) => {
            if (err.message !== mediaModule.CANCELLED_COMPONENT_UNMOUNTED) {
              setStep('upload-error');
              Sentry.captureException(
                new Error('error uploading image to cloudinary'),
                {
                  extra: {
                    error: err,
                  },
                }
              );
              throw err;
            }
          });
      }
    } else {
      createErrorNotification('UploadModal.error.fileType');
    }
  };

  const chooseImage = async (image: Image) => {
    const {width, height} = await getImageDimensions(image.url);

    setLastPickedFile({
      type: 'image',
      content: {name: image.filename},
      url: image.url,
      width,
      height,
      imageId: image.id,
      groupId: image.groupId,
    });
    onSetImage(image.url, {fileName: image.filename, width, height});
    if (mediaType === NINE_BY_SIXTEEN_IMAGE_ONLY) {
      setStep('success');
    } else {
      setIsCropperLoading(true);
      setStep('crop');
    }
  };

  const deleteImage = (id: number) => {
    deleteFromImageLibrary({groupId, imageId: id});
  };

  const retryUpload = () => {
    if (!lastPickedFile) {
      return;
    }
    if (lastPickedFile.type === 'file') {
      startUpload([lastPickedFile.content], {ignoreWarning: true});
    }
  };

  const goToLibrary = () => {
    setStep(initialStep);
  };

  const confirmCrop = () => {
    if (!imageUrl) {
      setStep('upload-error');
      return;
    }

    const cropper = cropperRef.current?.cropper;
    if (!cropper) {
      return;
    }
    const {
      x: cropX,
      y: cropY,
      width: cropWidth,
      height: cropHeight,
    } = cropper.getData(true);
    const {x, y, width, height} = intersect2D(
      {
        x: cropX,
        y: cropY,
        width: cropWidth,
        height: cropHeight,
      },
      {x: 0, y: 0, width: lastPickedFile?.width, height: lastPickedFile?.height}
    );
    const container = mapValues(contain(width, height, cropAspectRatio), (n) =>
      parseInt(n)
    );
    const resizedUrl = imageUrl.replace(
      '/image/upload/',
      // eslint-disable-next-line max-len
      `/image/upload/x_${x},y_${y},w_${width},h_${height},c_crop/w_${container.width},h_${container.height},c_pad/`
    );
    onSetCroppedImage(resizedUrl, {
      fileName: lastPickedFile?.content.name,
      width: container.width,
      height: container.height,
    });
    setStep('success');
  };

  const onConfirm = () => {
    if (lastPickedFile?.type === 'file' && imageUrl) {
      addImageToLibrary({
        url: imageUrl,
        filename: lastPickedFile.content.name,
        groupId,
        width: lastPickedFile.width,
        height: lastPickedFile.height,
      });
    } else if (lastPickedFile?.type === 'image') {
      trackImageUse({groupId, imageId: lastPickedFile.imageId});
    }

    onSaveChanges();
  };

  const getUploadPreviewStepCurried = ({
    hasNewUpload,
  }: {
    hasNewUpload: boolean;
  }) =>
    getUploadPreviewStep({
      type: 'image',
      intl,
      onClose: hasNewUpload ? onConfirm : onClose,
      body: (
        <ImagePreviewer
          imageUrl={imageUrl}
          croppedImageUrl={croppedImageUrl ?? imageUrl}
          showOriginalImage={showOriginalImage}
        />
      ),
      isRemovingEnabled,
      onRemove,
      startUpload,
      goToLibrary,
      hasNewUpload,
    });

  const steps = {
    initial: () =>
      getUploadStep({
        type: 'image',
        intl,
        onClose,
        hintTextKey,
        isUploading: false,
        onFilePick: startUpload,
      }),
    library: () =>
      getLibraryStep({
        libraryTab,
        setLibraryTab,
        imageLibrary: (
          <ImageLibrary
            key={libraryTab}
            startUpload={startUpload}
            chooseImage={chooseImage}
            deleteImage={deleteImage}
            setIsDeleteModalOpen={setIsDeleteModalOpen}
            isEduMeLibrary={libraryTab === MEDIA_LIBRARY_TABS.EDUME_IMAGES}
          />
        ),
        onClose,
        intl,
      }),
    'initial-seeded': () => getUploadPreviewStepCurried({hasNewUpload: false}),
    'file-size-warning': () =>
      getFileSizeWarningStep({
        type: 'image',
        intl,
        retryUpload,
        goBack: goToLibrary,
      }),
    'file-size-error': () =>
      getFileSizeErrorStep({type: 'image', intl, goBack: goToLibrary}),
    uploading: () =>
      getUploadStep({
        type: 'image',
        intl,
        onClose,
        hintTextKey,
        isUploading: true,
      }),
    'upload-error': () =>
      getUploadErrorStep({intl, onCancel: goToLibrary, retryUpload}),
    crop: () => ({
      title: intl.formatMessage({id: 'UploadModal.cropTitle'}),
      body: (
        <div className={styles['cropper-container']}>
          {isCropperLoading && (
            <LoadingIndicator
              indicator='hexagons'
              containerStyle='centerAbsolute'
            />
          )}
          <Cropper
            style={{height: '60vh'}}
            ready={() => setIsCropperLoading(false)}
            dragMode='move'
            modal={false}
            zoomOnWheel={false}
            src={lastPickedFile?.url}
            aspectRatio={cropAspectRatio}
            ref={cropperRef}
          />
          {showOriginalImage && (
            <HintBox
              title={intl.formatMessage({id: 'UploadModal.popoutHintTitle'})}
              text={intl.formatMessage({
                id: 'UploadModal.popoutHintDescription',
              })}
            />
          )}
          <div className={styles['zoom-buttons-container']}>
            <Button
              type='thirdary'
              darkIconUrl='/resources/img/24_add.svg'
              iconUrl='/resources/img/24_add.svg'
              noMarginLeft={true}
              noMarginTop={true}
              onClick={() => cropperRef.current?.cropper.zoom(0.1)}
            />
            <Button
              type='thirdary'
              darkIconUrl='/resources/img/24_minus.svg'
              iconUrl='/resources/img/24_minus.svg'
              noMarginLeft={true}
              noMarginTop={true}
              onClick={() => cropperRef.current?.cropper.zoom(-0.1)}
            />
          </div>
        </div>
      ),
      buttons: [
        {
          type: 'secondary',
          onClick: onClose,
          text: intl.formatMessage({id: 'Button.cancel'}),
          dataAutomation: 'cropper-cancel-button',
        },
        {
          type: 'primary',
          onClick: confirmCrop,
          text: intl.formatMessage({id: 'Button.okay'}),
          dataAutomation: 'cropper-done-button',
        },
      ],
      bottomLeftContent: {
        type: 'element',
        element: (
          <div>
            <Link
              text={intl.formatMessage({id: 'UploadModal.differentImageLink'})}
              onClick={goToLibrary}
            />
          </div>
        ),
      },
    }),
    success: () => getUploadPreviewStepCurried({hasNewUpload: true}),
  };

  return (
    <StyledModal
      onClose={onClose}
      content={steps[step]()}
      underlayClickExits={!isDeleteModalOpen}
      compact
    />
  );
};

export default UploadImageModal;
